/**
 * The Add Activity stepper's tokens, copy and day logic — lifted verbatim from
 * Adam's mockup (13 Oct) so the built thing and the approved design cannot
 * drift apart in the details that were actually argued over.
 *
 * Everything here is INLINE-STYLE values rather than Tailwind classes, for the
 * same reason the chip is: a class that is not emitted into the compiled CSS
 * fails silently and renders unstyled, and this file is the one place the
 * design's exact greens and greys live.
 */

/* ---------------------------------------------------------------- tokens -- */

export const T = {
    ink: '#18211F',
    inkSoft: '#2B3532',
    muted: '#5F6A67',
    mutedSoft: '#4E5956',
    faint: '#7A8582',
    line: '#EEF1F0',
    border: '#D5DCDA',
    borderSoft: '#C9D2CF',
    borderMenu: '#E1E6E4',
    green: '#1F5C50',
    greenDark: '#123E35',
    greenWash: '#E8F2EF',
    greenDisabled: '#B7C7C3',
    panel: '#F4F6F5',
    footer: '#FBFCFC',
    overlapWash: '#FBF0DC',
    white: '#FFFFFF',
}

/* The three event colours on the day list. Mediation blue and slot green are
 * the mockup's; the activity colour stays the board's violet so the dialog and
 * the calendar behind it agree. Adam asked to try darker blue and orange — both
 * are kept here so the swap is one line, not a hunt. */
export const EVENT_COLOR = {
    med: '#2459C7',
    slot: '#1E7A46',
    act: '#6D3FD1',
}
export const ACT_COLOR_OPTIONS = {
    purple: '#6D3FD1',
    darkBlue: '#1E3A8A',
    orange: '#C2410C',
}

/** Pill chip, selected and not. */
export const CHIP_ON = {
    borderColor: T.green,
    background: T.greenWash,
    color: T.greenDark,
    fontWeight: 600,
}
export const CHIP_OFF = {
    borderColor: T.border,
    background: T.white,
    color: T.inkSoft,
    fontWeight: 400,
}

export const STEP_LABELS = ['Details', 'Date & time', 'Your day']

export const SUBTITLE = [
    'Shows on your calendar and the case timeline',
    'Most activities don’t need a set time',
    'Check your day, then save',
]

/* ------------------------------------------------------------- the rules -- */

/** Minutes past midnight → "9:00 AM". */
export function fmtMinutes(mins) {
    if (mins == null || mins < 0) return ''
    const h = Math.floor(mins / 60)
    const m = String(mins % 60).padStart(2, '0')
    const suffix = h >= 12 ? 'PM' : 'AM'
    return `${h % 12 === 0 ? 12 : h % 12}:${m} ${suffix}`
}

/** "HH:MM" → minutes past midnight, or null. */
export function toMinutes(hhmm) {
    if (!hhmm) return null
    const [h, m] = String(hhmm).split(':')
    const hours = Number(h)
    const mins = parseInt(m, 10)
    if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null
    return hours * 60 + mins
}

/**
 * Which single event the message is about.
 *
 * PRIORITY, NOT ORDER — a booked mediation outranks another activity, which
 * outranks an open slot. A day can hold all three; the mediator should be told
 * about the most consequential one, not whichever happens to start first.
 */
export function pickConflict(hits) {
    return (
        hits.find((e) => e.kind === 'med') ||
        hits.find((e) => e.kind === 'act') ||
        hits.find((e) => e.kind === 'slot') ||
        null
    )
}

/**
 * The step-3 message and the choices under it.
 *
 * FIRST MATCH WINS, and the order is the whole argument from the 13 Oct call:
 *
 *   no events        → nothing to say beyond "your day is clear"
 *   no time set      → NOT a conflict. Adam: "if you have an NA on the time
 *                      then you don't have to really show this, because they
 *                      can do it at any point in the day." Informational only.
 *
 * THE WORDS ARE FLAT DECLARATIVES, not Adam's spoken phrasing. His line on the
 * call — "just so you're aware, you already have these scheduled that day, this
 * would be adding to that list" — is how someone talks, not how this product
 * writes: every sibling in this ladder is a short statement of fact, and the
 * hedge made that one read as borrowed from somewhere else. It also said less
 * than the panel directly above it, which already lists the day item by item.
 *   no overlap       → confirm and move on
 *   booked mediation → informational + confirm; the booking cannot be moved
 *                      from here, so the only choices are change the time or
 *                      proceed
 *   another activity → same shape, lighter words — two activities at once is
 *                      ordinary
 *   open slot        → the only REAL decision. The slot can still be booked by
 *                      a party, which is the call Adam described getting:
 *                      "I booked a slot and then the mediation got booked, now
 *                      I have a conflict."
 */
export function dayMessage({ events, start, timeLabel, conflict, isEditing }) {
    if (!events.length) {
        return { message: 'Nothing else is scheduled that day.', choices: [] }
    }
    /* EDITING NEVER ASKS.
     *
     * The activity is already on the day at that time — the mediator is
     * changing a note or a method, not creating the overlap, and nothing is
     * decided by answering. Making them confirm a clash they made last week
     * before they can fix a typo is the "warning while editing" Usman has now
     * cut twice: "we do not need to show warning while editing", and "we are
     * not adding, just updating details of already added activity."
     *
     * The day still SHOWS — that is the informative half, and it is the half
     * Adam asked for. Only the decision goes. */
    if (isEditing) {
        /* NOTHING AT ALL. Not a softer sentence — none. The panel above already
           shows the day, the activity is already on it, and there is no
           decision to put to the mediator, so any line here is a caption on a
           thing that needs no caption. */
        return { message: '', choices: [] }
    }
    if (start == null) {
        /* Nothing to say. Step 2 already told the mediator what no time means
           ("Without a time, this shows at the top of the day"), the panel above
           lists the day, and there is no decision — so a line here would only
           restate one of the two. The block is hidden entirely rather than left
           blank; see the step-3 render. */
        return { message: '', choices: [] }
    }
    if (!conflict) {
        return { message: 'No overlap. You’re good to save.', choices: [] }
    }
    if (conflict.kind === 'med') {
        return {
            message: `This falls during your ${conflict.desc} (${fmtMinutes(
                conflict.start
            )} to ${fmtMinutes(conflict.end)}). Please confirm you’d like to proceed.`,
            choices: ['Change time', `Proceed at ${timeLabel}`],
        }
    }
    if (conflict.kind === 'act') {
        return {
            message: `This overlaps your ${fmtMinutes(conflict.start)} ${
                conflict.desc
            }. Change the time or keep both.`,
            choices: ['Change time', `Keep ${timeLabel}`],
        }
    }
    return {
        message: `Your ${conflict.desc} (${fmtMinutes(
            conflict.start
        )} to ${fmtMinutes(
            conflict.end
        )}) is still open for mediation bookings. Keep it open, or close it off so it can’t be booked?`,
        choices: ['Keep it open', 'Close it off'],
    }
}

/** What the footer says once a decision is made, so the consequence is visible
 *  before the button is pressed rather than after. */
export function consequenceOf(choice) {
    if (choice === 'Close it off') return 'The slot closes when you save'
    if (choice === 'Keep it open') return 'The slot stays open for bookings'
    return ''
}
